import { useCallback, useState, useMemo } from 'react'
import type { Offer, OfferProduct, SheetContentOfferProps, PreFilteredProducts } from '@restaurant/core'
import { safeGetLogger } from '@restaurant/logging'

import {
  determineFlowType,
  createInitialFlowState,
  advanceFlow,
} from '@restaurant/core'
import type {
  IPlatformServices,
  IOffersService,
  OfferValidationResult,
  OfferClaimResult,
  OfferApplicationResult
} from '../contracts'

/**
 * OffersViewModel state interface
 */
export interface OffersViewModelState {
  // Core offer data
  offer: Offer | null
  loading: boolean
  error: string | null

  // Validation state
  validationResult: OfferValidationResult | null
  isValidatingOffer: boolean
  canClaimOffer: boolean
  validationErrors: string[]

  // Claim state
  claimResult: OfferClaimResult | null
  isClaimingOffer: boolean

  // Cart application state
  applyResult: OfferApplicationResult | null

  // Code-required offers
  enteredCode: string
  codeError: string | null

  // Terms and conditions
  termsAndConditions: string | null

  // Flow orchestration
  pendingOffer: Offer | null
  offerFlowState: 'idle' | 'required_products' | 'free_products' | 'complete'
  remainingRequiredProducts: OfferProduct[]
  remainingFreeProducts: OfferProduct[]
  currentProductIndex: number
  totalProductsInFlow: number
  itemsAddedDuringFlow: string[]
}

/**
 * OffersViewModel actions interface
 */
export interface OffersViewModelActions {
  // Core offer operations
  loadOffer: (offerId: string, userId?: string) => Promise<void>
  validateOffer: (userId: string, orderAmount: number) => Promise<void>
  claimOffer: (userId: string) => Promise<void>
  applyOfferToCart: () => Promise<void>
  loadTermsAndConditions: () => Promise<void>

  // Component integration
  getSheetContentOfferProps: () => Partial<SheetContentOfferProps>

  // Flow orchestration
  startOfferFlow: (preFiltered?: PreFilteredProducts) => void
  onRequiredProductAdded: (menuItemId: string, cartItemId: string) => void
  onFreeProductAdded: (menuItemId: string, cartItemId: string) => void
  cancelOfferFlow: () => void

  // Code-required offers
  setEnteredCode: (code: string) => void
  setCodeError: (error: string | null) => void

  // Utility actions
  clearError: () => void
  reset: () => void
}

/**
 * Combined OffersViewModel interface
 */
export interface OffersViewModel extends OffersViewModelState, OffersViewModelActions {}

/**
 * OffersViewModel dependencies interface
 */
export interface OffersViewModelDependencies {
  offersService?: IOffersService
}

/**
 * Platform-agnostic OffersViewModel factory
 * 
 * Creates a ViewModel for managing offer display, validation, claiming, and cart application.
 * Follows established cross-platform dependency injection patterns.
 * 
 * Features:
 * - Offer loading and display logic
 * - Real-time offer validation with order amount
 * - Offer claiming workflow with user authentication
 * - Cart application with discount calculations
 * - Terms and conditions loading
 * - Component props generation for SheetContentOffer
 * - Comprehensive error handling and loading states
 * 
 * @param services - Platform-specific services (navigation, platform, storage)
 * @param dependencies - Optional service dependencies for offer operations
 * @returns OffersViewModel hook factory
 */
export const createOffersViewModel = (
  services: IPlatformServices,
  dependencies?: OffersViewModelDependencies
) => {
  return (): OffersViewModel => {
    // Extract services
    const { navigationService } = services
    const offersService = dependencies?.offersService

    // Core offer state
    const [offer, setOffer] = useState<Offer | null>(null)
    const [loading, setLoading] = useState(!!offersService) // Start loading if service available
    const [error, setError] = useState<string | null>(null)

    // Validation state
    const [validationResult, setValidationResult] = useState<OfferValidationResult | null>(null)
    const [isValidatingOffer, setIsValidatingOffer] = useState(false)
    const [validationErrors, setValidationErrors] = useState<string[]>([])

    // Claim state
    const [claimResult, setClaimResult] = useState<OfferClaimResult | null>(null)
    const [isClaimingOffer, setIsClaimingOffer] = useState(false)

    // Cart application state
    const [applyResult, setApplyResult] = useState<OfferApplicationResult | null>(null)

    // Code-required offers
    const [enteredCode, setEnteredCode] = useState('')
    const [codeError, setCodeError] = useState<string | null>(null)

    // Terms and conditions
    const [termsAndConditions, setTermsAndConditions] = useState<string | null>(null)

    // Flow orchestration state
    const [pendingOffer, setPendingOffer] = useState<Offer | null>(null)
    const [offerFlowState, setOfferFlowState] = useState<'idle' | 'required_products' | 'free_products' | 'complete'>('idle')
    const [remainingRequiredProducts, setRemainingRequiredProducts] = useState<OfferProduct[]>([])
    const [remainingFreeProducts, setRemainingFreeProducts] = useState<OfferProduct[]>([])
    const [currentProductIndex, setCurrentProductIndex] = useState(0)
    const [totalProductsInFlow, setTotalProductsInFlow] = useState(0)
    const [itemsAddedDuringFlow, setItemsAddedDuringFlow] = useState<string[]>([])

    // Computed state
    const canClaimOffer = useMemo(() => {
      return !!(validationResult?.status === 'valid' && !isClaimingOffer)
    }, [validationResult, isClaimingOffer])

    // Core offer operations
    const loadOffer = useCallback(async (offerId: string, userId?: string) => {
      if (!offersService) {
        setError('Offers service not available')
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)
        
        const offerData = await offersService.getOfferDetails(offerId, userId)
        setOffer(offerData)
        
        // Reset validation state when loading new offer
        setValidationResult(null)
        setValidationErrors([])
        setClaimResult(null)
        setApplyResult(null)
        setEnteredCode('')
        setCodeError(null)

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load offer'
        setError(errorMessage)
        setOffer(null)
      } finally {
        setLoading(false)
      }
    }, [offersService])

    const validateOffer = useCallback(async (userId: string, orderAmount: number) => {
      if (!offersService || !offer) {
        return
      }

      try {
        setIsValidatingOffer(true)
        setError(null)
        
        const validation = await offersService.validateOffer(offer.id, userId, orderAmount)
        setValidationResult(validation)
        setValidationErrors(validation.status !== 'valid' && validation.message ? [validation.message] : [])
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to validate offer'
        setError(errorMessage)
        setValidationResult(null)
        setValidationErrors([])
      } finally {
        setIsValidatingOffer(false)
      }
    }, [offersService, offer])

    const claimOffer = useCallback(async (userId: string) => {
      if (!offersService || !offer) {
        return
      }

      try {
        setIsClaimingOffer(true)
        setError(null)
        
        const result = await offersService.claimOffer(offer.id, userId)
        setClaimResult(result)
        
        if (result.success) {
          safeGetLogger()?.offers('claimed', { success: true })
        }
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to claim offer'
        setError(errorMessage)
        setClaimResult(null)
      } finally {
        setIsClaimingOffer(false)
      }
    }, [offersService, offer])

    const applyOfferToCart = useCallback(async () => {
      if (!offersService || !offer) {
        return
      }

      try {
        setError(null)

        const result = await offersService.applyOfferToCart(offer.id, enteredCode)
        setApplyResult(result)
        if (result.success) {
          setCodeError(null)
          safeGetLogger()?.offers('applied', { discountAmount: result.discountApplied })
          navigationService.navigate('/cart')
          // Note: Sheet closing would be handled by platform-specific implementations
        } else if (result.message === 'Incorrect code.') {
          setCodeError('Incorrect code')
        }

      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to apply offer to cart'
        setError(errorMessage)
        setApplyResult(null)
      }
    }, [offersService, offer, navigationService, enteredCode])

    const loadTermsAndConditions = useCallback(async () => {
      if (!offersService || !offer) {
        return
      }

      try {
        setError(null)
        
        const terms = await offersService.getOfferTermsAndConditions(offer.id)
        setTermsAndConditions(terms)
        
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load terms and conditions'
        setError(errorMessage)
        setTermsAndConditions(null)
      }
    }, [offersService, offer])

    // Flow orchestration helpers
    const setFlowState = useCallback((flowState: {
      offerFlowState: 'idle' | 'required_products' | 'free_products' | 'complete'
      remainingRequiredProducts: OfferProduct[]
      remainingFreeProducts: OfferProduct[]
      currentProductIndex: number
      totalProductsInFlow: number
    }) => {
      setOfferFlowState(flowState.offerFlowState)
      setRemainingRequiredProducts(flowState.remainingRequiredProducts)
      setRemainingFreeProducts(flowState.remainingFreeProducts)
      setCurrentProductIndex(flowState.currentProductIndex)
      setTotalProductsInFlow(flowState.totalProductsInFlow)
    }, [])

    // Flow orchestration actions
    const startOfferFlow = useCallback((preFiltered?: PreFilteredProducts) => {
      if (!offer) return

      const flowType = determineFlowType(offer)

      if (flowType === 'direct') {
        // Percentage/amount - apply immediately
        applyOfferToCart()
        setOfferFlowState('complete')
        setPendingOffer(null)
        return
      }

      const initialState = createInitialFlowState(offer, preFiltered)
      setPendingOffer(offer)
      setFlowState(initialState)
      setItemsAddedDuringFlow([])
    }, [offer, applyOfferToCart, setFlowState])

    const onRequiredProductAdded = useCallback((menuItemId: string, cartItemId: string) => {
      const currentFlowState = {
        offerFlowState,
        remainingRequiredProducts,
        remainingFreeProducts,
        currentProductIndex,
        totalProductsInFlow,
      }

      const nextState = advanceFlow(currentFlowState, menuItemId, pendingOffer?.requirementType || offer?.requirementType)
      setFlowState(nextState)
      setItemsAddedDuringFlow(prev => [...prev, cartItemId])

      // BOGO: if we just transitioned to free_products, the UI layer should
      // check shouldAutoApplyFreeItem and call onFreeProductAdded if appropriate,
      // since the ViewModel doesn't have direct access to cart items.
    }, [offerFlowState, remainingRequiredProducts, remainingFreeProducts, currentProductIndex, totalProductsInFlow, setFlowState, pendingOffer, offer])

    const onFreeProductAdded = useCallback((menuItemId: string, cartItemId: string) => {
      const currentFlowState = {
        offerFlowState,
        remainingRequiredProducts,
        remainingFreeProducts,
        currentProductIndex,
        totalProductsInFlow,
      }

      const nextState = advanceFlow(currentFlowState, menuItemId)
      setFlowState(nextState)
      setItemsAddedDuringFlow(prev => [...prev, cartItemId])

      if (nextState.offerFlowState === 'complete') {
        // Apply the offer now that flow is done
        applyOfferToCart()
      }
    }, [offerFlowState, remainingRequiredProducts, remainingFreeProducts, currentProductIndex, totalProductsInFlow, setFlowState, applyOfferToCart])

    const cancelOfferFlow = useCallback(() => {
      setPendingOffer(null)
      setOfferFlowState('idle')
      setRemainingRequiredProducts([])
      setRemainingFreeProducts([])
      setCurrentProductIndex(0)
      setTotalProductsInFlow(0)
      setItemsAddedDuringFlow([])
    }, [])

    // Component integration
    const getSheetContentOfferProps = useCallback((): Partial<SheetContentOfferProps> => {
      if (loading) {
        return {
          offerDetailsProps: {
            offerTitle: 'Loading Offer...',
            offerDescription: 'Please wait while we load the offer details.',
            buttonText: 'Loading...',
            loading: true,
            disabled: true,
          },
        }
      }

      if (error) {
        return {
          offerDetailsProps: {
            offerTitle: 'Offer Unavailable',
            offerDescription: error,
            buttonText: 'Close',
            loading: false,
            disabled: true,
            onOfferPress: () => navigationService.goBack(),
          },
        }
      }

      if (!offer) {
        return {
          offerDetailsProps: {
            offerTitle: 'No Offer Selected',
            offerDescription: 'Please select an offer to view details.',
            buttonText: 'Browse Offers',
            loading: false,
            disabled: false,
            onOfferPress: () => {
              navigationService.navigate('/offers')
            },
          },
        }
      }

      // Determine button state and text
      let buttonText = 'View Offer'
      let disabled = true
      let onOfferPress: (() => void) | undefined

      if (isClaimingOffer) {
        buttonText = 'Claiming...'
        disabled = true
      } else if (claimResult?.success) {
        buttonText = 'Apply to Cart'
        disabled = false
        onOfferPress = applyOfferToCart
      } else if (canClaimOffer) {
        buttonText = 'Claim Offer'
        disabled = false
        onOfferPress = () => {
          safeGetLogger()?.warn('User ID needed for offer claiming', { domain: 'offers' })
        }
      } else if (validationResult && validationResult.status !== 'valid') {
        buttonText = 'Not Eligible'
        disabled = true
      }

      // Format offer description with validation info
      let offerDescription = offer.description
      if (validationResult?.status !== 'valid' && validationResult?.message) {
        offerDescription += `\n\nRequirements: ${validationResult.message}`
      }

      // finePrintText is built by the screen layer using generateOfferTerms()
      const finePrintText = ''

      return {
        imageProps: {
          imageUrl: offer.imageUrl,
          accessibilityLabel: `${offer.title} offer image`,
        },
        offerDetailsProps: {
          offerTitle: offer.title,
          offerDescription,
          buttonText,
          finePrintText,
          loading: isValidatingOffer || isClaimingOffer,
          disabled,
          onOfferPress,
        },
      }
    }, [
      loading,
      error,
      offer,
      isClaimingOffer,
      claimResult,
      canClaimOffer,
      validationResult,
      isValidatingOffer,
      termsAndConditions,
      navigationService,
      applyOfferToCart,
    ])

    // Utility actions
    const clearError = useCallback(() => {
      setError(null)
    }, [])

    const reset = useCallback(() => {
      setOffer(null)
      setLoading(false)
      setError(null)
      setValidationResult(null)
      setIsValidatingOffer(false)
      setValidationErrors([])
      setClaimResult(null)
      setIsClaimingOffer(false)
      setApplyResult(null)
      setEnteredCode('')
      setCodeError(null)
      setTermsAndConditions(null)
      setPendingOffer(null)
      setOfferFlowState('idle')
      setRemainingRequiredProducts([])
      setRemainingFreeProducts([])
      setCurrentProductIndex(0)
      setTotalProductsInFlow(0)
      setItemsAddedDuringFlow([])
    }, [])

    return {
      // State
      offer,
      loading,
      error,
      validationResult,
      isValidatingOffer,
      canClaimOffer,
      validationErrors,
      claimResult,
      isClaimingOffer,
      applyResult,
      enteredCode,
      codeError,
      termsAndConditions,

      // Flow orchestration state
      pendingOffer,
      offerFlowState,
      remainingRequiredProducts,
      remainingFreeProducts,
      currentProductIndex,
      totalProductsInFlow,
      itemsAddedDuringFlow,

      // Actions
      loadOffer,
      validateOffer,
      claimOffer,
      applyOfferToCart,
      loadTermsAndConditions,
      getSheetContentOfferProps,
      startOfferFlow,
      onRequiredProductAdded,
      onFreeProductAdded,
      cancelOfferFlow,
      setEnteredCode,
      setCodeError,
      clearError,
      reset,
    }
  }
}